import { AgentNode } from '../../../types/definition';
import { useAgents } from '../../../hooks/useAgents';
import { useModelConfigs } from '../../../hooks/useModelConfigs';
import { ModelConfigPickerOptions, ModelLifecycleNote, ModelPolicyNote } from '../../modelconfigs/ModelConfigPickerOptions';
import { useAccountModelPicks } from '../../../hooks/useAccountModels';
import { SchemaBuilder } from '../../schema-builder/SchemaBuilder';
import { InputMappingEditor } from './InputMappingEditor';

interface AgentNodePropertiesProps {
  node: AgentNode;
  onChange: (updates: Partial<AgentNode>) => void;
  /**
   * Inspect, do not edit (task 881). The lists are still fetched in read-only:
   * "which agent is this node?" is the commonest thing a reader opens the panel
   * for, and a select that cannot resolve its label shows a blank instead of a
   * name.
   */
  readOnly?: boolean;
}

export function AgentNodeProperties({ node, onChange, readOnly = false }: AgentNodePropertiesProps) {
  const { data: agentsData, isLoading: agentsLoading } = useAgents(1, 100);
  const { data: modelConfigsData, isLoading: modelConfigsLoading } = useModelConfigs(1, 100);
  // The account's model policy (task 1147): disallowed configs grey out, the default is marked.
  const modelPolicy = useAccountModelPicks();

  const agents = agentsData?.data || [];
  const modelConfigs = modelConfigsData?.data || [];

  return (
    <div className="space-y-4" aria-readonly={readOnly || undefined}>
      {/* Node Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          value={node.name || ''}
          onChange={readOnly ? undefined : (e) => onChange({ name: e.target.value })}
          disabled={readOnly}
          placeholder="Agent"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-blue-500 focus:ring-blue-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
      </div>

      {/* Agent Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Agent <span className="text-red-500">*</span>
        </label>
        <select
          value={node.agentId || ''}
          onChange={
            readOnly
              ? undefined
              : (e) => {
                  const selectedAgent = agents.find(a => a.id === e.target.value);
                  onChange({
                    agentId: e.target.value,
                    name: selectedAgent?.name || 'Agent',
                  });
                }
          }
          disabled={readOnly || agentsLoading}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-blue-500 focus:ring-blue-500
                     dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="">Select an agent...</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>
        {!node.agentId && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            An agent must be selected for this node to execute
          </p>
        )}
      </div>

      {/* Alias */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Alias (optional)
        </label>
        <input
          type="text"
          value={node.alias || ''}
          onChange={readOnly ? undefined : (e) => onChange({ alias: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="Local name for this agent"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-blue-500 focus:ring-blue-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Reference name in template expressions (defaults to agent name)
        </p>
      </div>

      {/* Model Config Override — the definition key stays `llmConfigId` (the service's
          frozen definition format predates the Model rename, task 1073). */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Model Config Override (optional)
        </label>
        <select
          value={node.llmConfigId || ''}
          onChange={readOnly ? undefined : (e) => onChange({ llmConfigId: e.target.value || undefined })}
          disabled={readOnly || modelConfigsLoading}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-blue-500 focus:ring-blue-500
                     dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="">Use agent default</option>
          <ModelConfigPickerOptions
            configs={modelConfigs}
            selectedId={node.llmConfigId}
            policy={modelPolicy}
            label={(config) => config.name || `${config.model.provider?.key ?? 'unknown'}/${config.model.modelId}`}
          />
        </select>
        <ModelLifecycleNote configs={modelConfigs} selectedId={node.llmConfigId} />
        <ModelPolicyNote configs={modelConfigs} selectedId={node.llmConfigId} policy={modelPolicy} />
      </div>

      {/* Entry Point Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Entry Point
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Re-executes on collaboration continuation
          </p>
        </div>
        <button
          type="button"
          onClick={readOnly ? undefined : () => onChange({ entry: !node.entry })}
          disabled={readOnly}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${node.entry ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
              transition duration-200 ease-in-out
              ${node.entry ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
      </div>

      {/* Autonomy Settings */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Autonomy Settings
        </h4>

        {/* Max Iterations */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Max Iterations
          </label>
          <input
            type="number"
            min="1"
            value={node.autonomy?.maxIterations || ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    const val = e.target.value ? parseInt(e.target.value) : undefined;
                    onChange({
                      autonomy: { ...node.autonomy, maxIterations: val }
                    });
                  }
            }
            disabled={readOnly}
            placeholder="No limit"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-blue-500 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>

        {/* Confidence Threshold */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Confidence Threshold (0-1)
          </label>
          <input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={node.autonomy?.confidenceThreshold ?? ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    const val = e.target.value ? parseFloat(e.target.value) : undefined;
                    onChange({
                      autonomy: { ...node.autonomy, confidenceThreshold: val }
                    });
                  }
            }
            disabled={readOnly}
            placeholder="Not set"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-blue-500 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>

        {/* Quality Threshold */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Quality Threshold (0-1)
          </label>
          <input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={node.autonomy?.qualityThreshold ?? ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    const val = e.target.value ? parseFloat(e.target.value) : undefined;
                    onChange({
                      autonomy: { ...node.autonomy, qualityThreshold: val }
                    });
                  }
            }
            disabled={readOnly}
            placeholder="Not set"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-blue-500 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>

        {/* Satisfaction Check Toggle */}
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Satisfaction Check
          </label>
          <button
            type="button"
            onClick={readOnly ? undefined : () => onChange({
              autonomy: { ...node.autonomy, satisfactionCheck: !node.autonomy?.satisfactionCheck }
            })}
            disabled={readOnly}
            className={`
              relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${node.autonomy?.satisfactionCheck ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}
            `}
          >
            <span
              className={`
                pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${node.autonomy?.satisfactionCheck ? 'translate-x-4' : 'translate-x-0'}
              `}
            />
          </button>
        </div>

        {/* Allow Data Requests Toggle */}
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Allow Data Requests
          </label>
          <button
            type="button"
            onClick={readOnly ? undefined : () => onChange({
              autonomy: { ...node.autonomy, allowDataRequests: !node.autonomy?.allowDataRequests }
            })}
            disabled={readOnly}
            className={`
              relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${node.autonomy?.allowDataRequests ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}
            `}
          >
            <span
              className={`
                pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${node.autonomy?.allowDataRequests ? 'translate-x-4' : 'translate-x-0'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Input Schema */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <SchemaBuilder
          label="Input Schema"
          value={node.input ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ input: schema ?? undefined })}
        />
      </div>

      {/* Input Mapping */}
      <div>
        <InputMappingEditor
          value={node.inputMapping}
          readOnly={readOnly}
          onChange={(mapping) => onChange({ inputMapping: mapping })}
          schema={node.input}
        />
      </div>

      {/* Output Schema */}
      <div>
        <SchemaBuilder
          label="Output Schema"
          value={node.output ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ output: schema ?? undefined })}
        />
      </div>
    </div>
  );
}
